class MediaSliderWithText extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-media-slider-track]');

    if (!this.track) return;

    this.slideItems = [...this.querySelectorAll('[data-media-slider-item]')];
    this.controls = [...this.querySelectorAll('[data-media-slider-control]')];
    this.previousControl = this.querySelector('[data-media-slider-control="previous"]');
    this.nextControl = this.querySelector('[data-media-slider-control="next"]');
    this.track.addEventListener('pointerdown', this.handlePointerDown);
    this.track.addEventListener('pointermove', this.handlePointerMove);
    this.track.addEventListener('pointerup', this.handlePointerUp);
    this.track.addEventListener('pointercancel', this.handlePointerUp);
    this.track.addEventListener('scroll', this.handleScroll, { passive: true });
    this.controls.forEach((control) => control.addEventListener('click', this.handleControlClick));

    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(this.handleResize);
      this.resizeObserver.observe(this.track);
      this.slideItems.forEach((item) => this.resizeObserver.observe(item));
    }

    this.updateControls();
  }

  disconnectedCallback() {
    this.track?.removeEventListener('pointerdown', this.handlePointerDown);
    this.track?.removeEventListener('pointermove', this.handlePointerMove);
    this.track?.removeEventListener('pointerup', this.handlePointerUp);
    this.track?.removeEventListener('pointercancel', this.handlePointerUp);
    this.track?.removeEventListener('scroll', this.handleScroll);
    this.controls?.forEach((control) => control.removeEventListener('click', this.handleControlClick));
    this.resizeObserver?.disconnect();
    cancelAnimationFrame(this.scrollFrame);
    cancelAnimationFrame(this.dragFrame);
  };

  handlePointerDown = (event) => {
    if (event.pointerType !== 'mouse' || event.button !== 0) return;
    if (event.target.closest('a, button, input, video')) return;

    event.preventDefault();

    this.dragState = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startScrollLeft: this.track.scrollLeft,
      targetScrollLeft: this.track.scrollLeft,
    };

    this.track.classList.add('is-dragging');
    this.track.setPointerCapture(event.pointerId);
  };

  handlePointerMove = (event) => {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

    event.preventDefault();

    const delta = event.clientX - this.dragState.startX;
    this.dragState.targetScrollLeft = this.dragState.startScrollLeft - delta;
    this.requestDragFrame();
  };

  handlePointerUp = (event) => {
    if (!this.dragState || event.pointerId !== this.dragState.pointerId) return;

    cancelAnimationFrame(this.dragFrame);
    this.track.scrollLeft = this.dragState.targetScrollLeft;
    this.track.classList.remove('is-dragging');
    if (this.track.hasPointerCapture(event.pointerId)) {
      this.track.releasePointerCapture(event.pointerId);
    }
    this.dragState = null;
    this.snapToNearestCard();
    this.updateControls();
  };

  handleScroll = () => {
    cancelAnimationFrame(this.scrollFrame);
    this.scrollFrame = requestAnimationFrame(this.updateControls);
  };

  handleResize = () => {
    this.updateControls();
  };

  handleControlClick = (event) => {
    const direction = event.currentTarget?.dataset.mediaSliderControl;

    if (direction !== 'previous' && direction !== 'next') return;

    this.scrollToCard(direction);
  };

  requestDragFrame() {
    if (this.dragFrame) return;

    this.dragFrame = requestAnimationFrame(() => {
      this.track.scrollLeft = this.dragState?.targetScrollLeft ?? this.track.scrollLeft;
      this.dragFrame = null;
    });
  }

  scrollToCard(direction) {
    const positions = this.getSlidePositions();
    if (!positions.length) return;

    const currentPosition = this.track.scrollLeft;
    const tolerance = 2;
    let targetPosition = positions[0];

    if (direction === 'next') {
      targetPosition = positions.find((position) => position > currentPosition + tolerance) ?? positions[positions.length - 1];
    } else {
      targetPosition =
        positions
          .slice()
          .reverse()
          .find((position) => position < currentPosition - tolerance) ?? positions[0];
    }

    this.track.scrollTo({
      left: targetPosition,
      behavior: 'smooth',
    });
  }

  snapToNearestCard() {
    const positions = this.getSlidePositions();
    if (!positions.length) return;

    const currentPosition = this.track.scrollLeft;
    const targetPosition = positions.reduce((nearest, position) => {
      return Math.abs(position - currentPosition) < Math.abs(nearest - currentPosition) ? position : nearest;
    }, positions[0]);

    this.track.scrollTo({
      left: targetPosition,
      behavior: 'smooth',
    });
  }

  getSlidePositions() {
    const maxScrollLeft = this.getMaxScrollLeft();
    const trackStartInset = this.getTrackStartInset();

    return this.slideItems.map((item) => {
      return Math.min(Math.max(item.offsetLeft - this.track.offsetLeft - trackStartInset, 0), maxScrollLeft);
    });
  }

  getMaxScrollLeft() {
    return Math.max(this.track.scrollWidth - this.track.clientWidth, 0);
  }

  getTrackStartInset() {
    const styles = getComputedStyle(this.track);

    return parseFloat(styles.paddingInlineStart || styles.paddingLeft) || 0;
  }

  updateControls = () => {
    const maxScrollLeft = this.getMaxScrollLeft();
    const canScroll = maxScrollLeft > 1;
    const atStart = this.track.scrollLeft <= 1;
    const atEnd = this.track.scrollLeft >= maxScrollLeft - 1;

    this.classList.toggle('is-slider-static', !canScroll);
    this.classList.toggle('has-slider-progress', this.track.scrollLeft > 8);

    if (this.previousControl) {
      this.previousControl.disabled = !canScroll || atStart;
    }

    if (this.nextControl) {
      this.nextControl.disabled = !canScroll || atEnd;
    }
  };
}

if (!customElements.get('media-slider-with-text')) {
  customElements.define('media-slider-with-text', MediaSliderWithText);
}
